import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { TranslateService } from '@ngx-translate/core';
import { TreeNode } from 'primeng/api';
import { TreeNodeSelectEvent } from 'primeng/tree/tree.interface';
import { finalize, firstValueFrom, forkJoin, of, switchMap } from 'rxjs';

import { AuthService } from 'src/app/_service/auth.service';
import { CompanyService } from 'src/app/_service/company.service';
import { VesselService } from 'src/app/_service/vessel.service';
import { getCompanyData } from 'src/app/_model/company';
import {
  getAddons,
  getApplications,
  ids,
  postVesselApplications,
  postVesselData,
  vesselForm,
} from 'src/app/_model/vessel';
import * as routing from 'src/app/_model/routing';

type UploadStatus = 'initial' | 'uploading' | 'success' | 'fail';
type SubscriptionNodeType = 'application' | 'addon';

interface VesselApplicationResponse {
  application_id: string;
  addon: string[];
}

interface ApplicationTreeNode extends TreeNode {
  key: string;
  label: string;
  data: string;
  nodeType: SubscriptionNodeType;
  applicationId: string;
  addonId?: string;
  company?: unknown;
  children?: ApplicationTreeNode[];
  parent?: ApplicationTreeNode;
}

@Component({
  selector: 'app-vessel-edit',
  templateUrl: './vessel-edit.component.html',
  styleUrls: ['./vessel-edit.component.css']
})
export class VesselEditComponent implements OnInit, OnDestroy {
  token = localStorage.getItem('token');

  form = new vesselForm();
  vesselForm = this.form.defaultForm;
  editVesselData!: postVesselData;

  isEditMode = false;
  isReadOnly = true;
  isSubmitting = false;
  isTreeLoading = false;
  isTreeReady = false;
  isCompleted = false;
  updated = false;

  shortID = '';
  selectedVesselType = '';

  equipList: unknown[] = [];
  equipLoading = false;

  status: UploadStatus = 'initial';
  file: File | null = null;
  image: File | null = null;
  selectedImage = '../assets/images/icon-photo.png';
  isImageChanged = false;
  hasImage = false;

  ownerList: getCompanyData[];
  managerList: getCompanyData[];
  exownerList: getCompanyData[];
  exmanagerList: getCompanyData[];
  builderList: getCompanyData[];
  maintainerList: getCompanyData[];

  selectedManager = '';
  selectedOwner = '';
  selectedBuilder = '';
  selectedMaintainer = '';
  selectedexowner = '';
  selectedexmanager = '';

  applicationList: getApplications[] = [];
  addonList: getAddons[] = [];

  mainList: any[] = [];

  primeNgDataset: ApplicationTreeNode[] = [];
  primeNgTreeDataSet: ApplicationTreeNode[] = [];
  selectedNodes: ApplicationTreeNode[] = [];
  receiveData: VesselApplicationResponse[] = [];

  nodes: any[] = [];
  condition = true;
  selectedList = new Map<string, string>();
  selectedcapacityList = new Map<string, string>();

  readonly vesselTypes = [{ type: 'Vessel' }, { type: 'SEP' }];
  readonly engineTypes = [{ type: 'V' }, { type: 'S' }];
  readonly structuralDesignTypes = [
    { type: 'Monohull' },
    { type: 'Multihull' },
    { type: 'Double Hull' },
  ];
  readonly operationalStatus = [
    { type: 'In operation' },
    { type: 'Not in operation' },
  ];
  readonly RudderTypes = [
    { type: 'Balanced' },
    { type: 'Spade' },
    { type: 'Skeg Hung' },
    { type: 'Full Spade' },
  ];
  readonly PropellerTypes = [{ type: 'CPP' }, { type: 'FPP' }];
  readonly classificationSocietyType = [
    { type: 'NK' },
    { type: 'DNV' },
    { type: 'ABS' },
    { type: 'BV' },
    { type: 'CCS' },
    { type: 'CR' },
    { type: 'JG' },
    { type: 'KR' },
    { type: 'LR' },
    { type: 'RINA' },
    { type: 'Other' },
  ];
  readonly engineFoUnitType = [
    { type: 'l (liter)', value: 'l' },
    { type: 'kg', value: 'kg' },
    { type: 'ton', value: 'ton' },
    { type: 'gal (gallon)', value: 'gal' },
    { type: 'bar (barrel)', value: 'bar' },
    { type: 'drum', value: 'drum' },
  ];
  readonly generatorFoUnitType = [
    { type: 'l (liter)', value: 'l' },
    { type: 'kg', value: 'kg' },
    { type: 'ton', value: 'ton' },
    { type: 'gal (gallon)', value: 'gal' },
    { type: 'bar (barrel)', value: 'bar' },
    { type: 'drum', value: 'drum' },
  ];

  constructor(
    private location: Location,
    private companyService: CompanyService,
    private vesselService: VesselService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    public auth: AuthService,
  ) { 
    this.ownerList = [...this.companyService.companyList];
    this.managerList = [...this.companyService.companyList];
    this.exownerList = [...this.companyService.companyList];
    this.exmanagerList = [...this.companyService.companyList];
    this.builderList = [...this.companyService.companyList];
    this.maintainerList = [...this.companyService.companyList];

    this.registerIcons();
  }
  
  ngOnInit(): void {
    if (this.vesselService.editVesselFlag) {
      this.initializeExistingVessel(this.vesselService.editVesselData);
      return;
    }

    this.getCurrVesselData();
  }

  ngOnDestroy(): void {
    this.vesselForm.reset();
  }

  private registerIcons(): void {
    const icons = ['vessel', 'zoom', 'zoomin'];

    for (const icon of icons) {
      this.iconRegistry.addSvgIcon(
        icon,
        this.sanitizer.bypassSecurityTrustResourceUrl(`/assets/${icon}.svg`),
      );
    }
  }

  private initializeExistingVessel(editData: postVesselData): void {
    this.setEditVesselData(editData);
    this.getSelectedVesselId();
    this.getImage();
    this.loadApplicationTree();
  }

  getCurrVesselData(): void {
    const vesselId = window.location.pathname.split('/').at(-1);

    if (!vesselId) {
      console.error('Unable to determine the vessel ID from the URL.');
      return;
    }

    this.vesselService.getVesselById(vesselId).subscribe({
      next: (value: any) => {
        this.addToVesselData(value, vesselId);
        this.initializeExistingVessel(value);
      },
      error: (error: unknown) => {
        console.error('Error fetching vessel:', error);
      },
    });
  }

  addToVesselData(data: postVesselData, vesselId: string): void {
    this.vesselService.selectedVesselID = vesselId;
    this.vesselService.editVesselFlag = true;
    this.vesselService.editVesselData = data;
  }

  compareVesselTypes(type1: string | null, type2: string | null): boolean {
    return (type1 ?? '').toLowerCase() === (type2 ?? '').toLowerCase();
  }
  
  setEditVesselData(editData: postVesselData) {
    this.vesselForm.patchValue(editData);
  }

  onClickVessels() {
    this.router.navigate([routing.VesselList]);
  }

  getSelectedVesselId(): void {
    this.vesselService
      .uuidlongtoshort(this.vesselService.selectedVesselID)
      .subscribe({
        next: (value: any) => {
          this.shortID = value.shortuuid;
        },
        error: (error: unknown) => {
          console.error('Unable to get short vessel ID:', error);
        },
      });
  }

  onOpenedChange(status: any, type: String) {

  }

  filterCompany(searchValue: any, type: String): void {
    const query = String(searchValue?.target?.value ?? '').toLowerCase();
    const filteredCompanies = this.companyService.companyList.filter((company) =>
      company?.name?.toLowerCase().startsWith(query),
    );

    switch (type) {
      case 'owner':
        this.ownerList = filteredCompanies;
        break;
      case 'manager':
        this.managerList = filteredCompanies;
        break;
      case 'exowner':
        this.exownerList = filteredCompanies;
        break;
      case 'exmanager':
        this.exmanagerList = filteredCompanies;
        break;
      case 'builder':
        this.builderList = filteredCompanies;
        break;
      case 'maintainer':
        this.maintainerList = filteredCompanies;
        break;
    }
  }

  managerSelection(event: any): void {
    this.selectedManager = event.value;
    this.vesselForm.patchValue({ manager_id: this.selectedManager });
  }

  ownerSelection(event: any): void {
    this.selectedOwner = event.value;
    this.vesselForm.patchValue({ owner_id: this.selectedOwner });
  }

  onClickClear(event: Event): void {
    this.selectedOwner = '';
    this.vesselForm.patchValue({ owner_id: null });
    event.stopPropagation();
  }

  builderSelection(event: any): void {
    this.selectedBuilder = event.value;
    this.vesselForm.patchValue({ builder_id: this.selectedBuilder });
  }

  maintainerSelection(event: any): void {
    this.selectedMaintainer = event.value;
    this.vesselForm.patchValue({ maintener_id: this.selectedMaintainer });
  }

  exownerSelection(event: any): void {
    this.selectedexowner = event.value;
    this.vesselForm.patchValue({ exowner_id: this.selectedexowner });
  }

  exmanagerSelection(event: any): void {
    this.selectedexmanager = event.value;
    this.vesselForm.patchValue({ exmanager_id: this.selectedexmanager });
  }

  //Upload image
  onSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.image = file;

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        return;
      }

      this.selectedImage = reader.result;
      this.isImageChanged = true;
      this.hasImage = true;
    };
    reader.readAsDataURL(file);
  }
  async onImageUpload() {
    if (!this.image) return true;
    
    let imgExtension: string | undefined = '';
    const imgNameParts = this.image?.name?.split('.');
    if (imgNameParts.length > 1) {
      imgExtension = imgNameParts.pop();
      this.vesselForm.patchValue({
        thumbnailExtension: imgExtension,
      });
    }
    try {
      await firstValueFrom(this.vesselService.putVesselThumbnail(this.image));
      return true;
    } catch (err) {
      window.alert(this.translate.instant("imageUploadErr"));
      return false;
    }

    // if (this.image) {
    //   const uploadImage = this.vesselService.putVesselThumbnail(this.image);
    //   uploadImage.subscribe({
    //     error:(err) => {
    //       console.log("Error Occured: " + err);
    //     },
    //     complete:() => {
    //       console.log("Image Uploaded");
    //     },
    //   })
    // }
  }
  getImage() {
    this.vesselService.getVesselThumbnail().subscribe(
      {
        next: (response: any) => {
          const blob = new Blob([response], { type: 'image/png' });
          const url = window.URL.createObjectURL(blob);
  
          const img = new Image();
          img.src = url;

          if (response?.type !== "application/json") {
            this.selectedImage = url;
            this.hasImage = true;
          }
          const container = document.getElementById('imageContainer');
          if (container && img) {
            container.innerHTML = '';
            container.appendChild(img);
          }
        },
        error: (error: any) => {
          console.error('Error loading image', error);
        },
      }
    );
  }

  getCompanyName(id: any) {
    const company = this.companyService.companyList.find(
      (item) => item.id === id,
    );

    return company?.name || company?.otherName || '';
  }
  
  //vessel mapping
  onChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.status = 'initial';
    this.file = file;
  }

  onUpload() {
    if (!this.file || !this.shortID) {
      return;
    }

    this.status = 'uploading';

    this.vesselService.uploadFile(this.file, this.shortID).subscribe({
      next: () => {
        this.status = 'success';
      },
      error: (error: unknown) => {
        console.error('Vessel mapping upload failed:', error);
        this.status = 'fail';
      },
    });
  }

  loadApplicationTree(): void {
    const vesselId = this.vesselService.selectedVesselID;

    if (!vesselId) {
      console.error('Cannot initialize application tree without a vessel ID.');
      return;
    }

    this.isTreeLoading = true;
    this.isTreeReady = false;
    this.isCompleted = false;

    forkJoin({
      applications: this.vesselService.getApplications(),
      addons: this.vesselService.getAddon(),
      selectedApplications:
        this.vesselService.getVesselApplicationList(vesselId),
    })
      .pipe(
        finalize(() => {
          this.isTreeLoading = false;
        }),
      )
      .subscribe({
        next: ({ applications, addons, selectedApplications }) => {
          this.applicationList = [...applications];
          this.addonList = [...addons];
          this.receiveData = [...(selectedApplications ?? [])];

          this.mainList = this.createTreeFormat(
            this.applicationList,
            this.addonList,
          );

          this.primeNgDataset = this.createTreeFormatPrimeNg(
            this.applicationList,
            this.addonList,
          );

          this.cdr.detectChanges();

          this.selectedNodes = this.resolveSelectedNodes(this.receiveData);
          this.rebuildPrimeNgTreeDataSet();
          this.isTreeReady = true;
          this.isCompleted = true;
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          console.error('Failed to initialize application tree:', error);
          this.primeNgDataset = [];
          this.selectedNodes = [];
          this.isTreeReady = true;
          this.isCompleted = true;
        },
      });
  }

  createTreeFormat(
    applicationList: getApplications[],
    addonList: getAddons[],
  ): any[] {
    return applicationList.map((application, applicationIndex) => ({
      key: String(application.id),
      id: application.id,
      name: application.name,
      description: application.description,
      parent: 'Subscription',
      children: addonList.map((addon) => ({
        key: `${application.id}:${addon.id}`,
        id: addon.id,
        name: addon.name,
        description: addon.description,
        company: addon.company,
        applicationIndex,
      })),
    }));
  }

  createTreeFormatPrimeNg(
    applicationList: getApplications[],
    addonList: getAddons[],
  ): ApplicationTreeNode[] {
    return applicationList.map((application) => {
      const applicationId = String(application.id);

      const parentNode: ApplicationTreeNode = {
        key: `application:${applicationId}`,
        data: applicationId,
        label: application.name,
        nodeType: 'application',
        applicationId,
        parent: undefined,
        children: [],
      };

      parentNode.children = addonList.map((addon) => {
        const addonId = String(addon.id);

        return {
          key: `application:${applicationId}:addon:${addonId}`,
          data: addonId,
          label: addon.name,
          nodeType: 'addon',
          applicationId,
          addonId,
          company: addon.company,
          parent: parentNode,
        };
      });

      return parentNode;
    });
  }

  resolveSelectedNodes(
    savedSelections: VesselApplicationResponse[],
  ): ApplicationTreeNode[] {
    const selectedByKey = new Map<string, ApplicationTreeNode>();

    for (const savedSelection of savedSelections) {
      const applicationId = String(savedSelection.application_id);
      const applicationNode = this.primeNgDataset.find(
        (node) => node.applicationId === applicationId,
      );

      if (!applicationNode) {
        console.warn(
          'Saved application was not found in the current application list:',
          savedSelection.application_id,
        );
        continue;
      }

      selectedByKey.set(applicationNode.key, applicationNode);

      const selectedAddonIds = new Set(
        (savedSelection.addon ?? []).map(String),
      );

      for (const child of applicationNode.children ?? []) {
        if (child.addonId && selectedAddonIds.has(child.addonId)) {
          selectedByKey.set(child.key, child);
        }
      }
    }

    return [...selectedByKey.values()];
  }



  //get addon list from the api
  getAddons(){
    this.vesselService.getAddon().subscribe({
      next: (value:any) => {
        value.forEach((element:any) => {this.addonList.push(element)});
        this.mainList = this.createTreeFormat(this.applicationList,this.addonList);
        this.primeNgDataset = this.createTreeFormatPrimeNg(this.applicationList,this.addonList); 
      },
      error: (error) => {
        console.error(error);
      },
    })
  }

  onNodeSelect(event: TreeNodeSelectEvent): void {
    const selectedNode = event.node as ApplicationTreeNode;

    Promise.resolve().then(() => {
      const nextSelection = [...this.selectedNodes, selectedNode];

      if (selectedNode.nodeType === 'addon' && selectedNode.parent) {
        nextSelection.push(selectedNode.parent);
      }

      this.selectedNodes = this.uniqueNodes(nextSelection);
      this.rebuildPrimeNgTreeDataSet();
    });
  }

  onNodeUnselect(event: TreeNodeSelectEvent): void {
    const unselectedNode = event.node as ApplicationTreeNode;

    Promise.resolve().then(() => {
      let nextSelection = this.selectedNodes.filter(
        (node) => node.key !== unselectedNode.key,
      );

      if (unselectedNode.nodeType === 'application') {
        nextSelection = nextSelection.filter(
          (node) =>
            !(
              node.nodeType === 'addon' &&
              node.applicationId === unselectedNode.applicationId
            ),
        );
      }

      this.selectedNodes = this.uniqueNodes(nextSelection);
      this.rebuildPrimeNgTreeDataSet();
    });
  }

  uniqueNodes(
    nodes: ApplicationTreeNode[],
  ): ApplicationTreeNode[] {
    const nodesByKey = new Map<string, ApplicationTreeNode>();

    for (const node of nodes) {
      if (node?.key) {
        nodesByKey.set(node.key, node);
      }
    }

    return [...nodesByKey.values()];
  }

  buildApplicationSelection(): ids[] {
    const addonsByApplication = new Map<string, Set<string>>();

    for (const node of this.selectedNodes) {
      if (node.nodeType === 'application') {
        if (!addonsByApplication.has(node.applicationId)) {
          addonsByApplication.set(node.applicationId, new Set<string>());
        }
        continue;
      }

      if (!node.addonId) {
        continue;
      }

      if (!addonsByApplication.has(node.applicationId)) {
        addonsByApplication.set(node.applicationId, new Set<string>());
      }

      addonsByApplication.get(node.applicationId)!.add(node.addonId);
    }

    return [...addonsByApplication.entries()].map(
      ([applicationId, addonIds]) => ({
        application_id: applicationId,
        addon: [...addonIds],
      }),
    );
  }

  get selectedIds(): ids[] {
    return this.buildApplicationSelection();
  }

  rebuildPrimeNgTreeDataSet(): void {
  const expandedState = new Map(
    this.primeNgTreeDataSet.map((node) => [
      node.applicationId,
      node.expanded,
    ]),
  );

  const selectedChildrenByApplication = new Map<string, Set<string>>();

  for (const selection of this.buildApplicationSelection()) {
    selectedChildrenByApplication.set(
      String(selection.application_id),
      new Set((selection.addon ?? []).map(String)),
    );
  }

  this.primeNgTreeDataSet = this.primeNgDataset
    .filter((applicationNode) =>
      selectedChildrenByApplication.has(applicationNode.applicationId),
    )
    .map((applicationNode) => {
      const selectedAddonIds =
        selectedChildrenByApplication.get(
          applicationNode.applicationId,
        ) ?? new Set<string>();

      const node: ApplicationTreeNode = {
        ...applicationNode,

        // New nodes start expanded.
        // Existing nodes keep whatever the user chose.
        expanded:
          expandedState.get(applicationNode.applicationId) ?? true,

        children: [],
      };

      node.children = (applicationNode.children ?? [])
        .filter(
          (child) =>
            child.addonId &&
            selectedAddonIds.has(child.addonId),
        )
        .map((child) => ({
          ...child,
          parent: node,
        }));

      return node;
    });
}

  selectedItemSetup(_dataset?: unknown[]): ids[] {
    return this.buildApplicationSelection();
  }

  addApplications(vesselId: string): void {
  const selections = this.buildApplicationSelection();

  this.isSubmitting = true;

  this.vesselService
    .resetVesselApplications(vesselId)
    .pipe(
      switchMap(() => {
        if (selections.length === 0) {
          return of([]);
        }

        const requests = selections.map((selection) => {
          const payload: postVesselApplications = {
            vessel_id: vesselId,
            application_id: selection.application_id,
            addon: selection.addon,
          };

          return this.vesselService.submitVesselApplications(payload);
        });

        return forkJoin(requests);
      }),

      finalize(() => {
        this.isSubmitting = false;
      }),
    )
    .subscribe({
      next: () => {
        this.receiveData = selections.map((selection) => ({
          application_id: selection.application_id,
          addon: [...selection.addon],
        }));

        window.alert(this.translate.instant('updated'));
      },

      error: (error: unknown) => {
        console.error(
          'Failed to update vessel applications:',
          error,
        );

        window.alert(this.translate.instant('err'));
      },
    });
}

  updateSubscription(): void {
    if (this.auth.isYakuin) {
      window.alert(this.translate.instant('noPermission'));
      return;
    }

    this.addApplications(this.vesselService.selectedVesselID);
  }

  jumpCreateVessel() {
    this.router.navigate([routing.VesselRegistration]);
  }

  async onSubmit() {
    if (!this.checkSubmit()) {
      return;
    }

    if (this.auth.isYakuin) {
      window.alert(this.translate.instant('noPermission'));
      return;
    }

    if (!this.isEditMode) {
      return;
    }

    const imageUploadSuccessful = await this.onImageUpload();
    if (!imageUploadSuccessful) {
      return;
    }

    this.isSubmitting = true;

    this.vesselService
      .submitExistingVessel(this.vesselForm.value)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe({
        next: () => {
          window.alert(this.translate.instant('updated'));
          this.isEditMode = false;
          this.updated = true;
        },
        error: (error: unknown) => {
          console.error('Failed to update vessel:', error);
          window.alert(this.translate.instant('err'));
        },
      });
  }

  onNewEquip() {

  }

  sortItems(field: string, order: 'asc' | 'desc') {

  }
  
  checkSubmit(): boolean {
    return window.confirm('Can I register this?');
  }

  onClose() {
    this.vesselService.editVesselFlag = false;
    this.location.back();
  }

  goBack() {
    this.location.back();
  }

  onDelete() {
    if (this.auth.isYakuin) {
      window.alert(this.translate.instant('noPermission'));
      return;
    }

    if (!this.checkDelete()) {
      return;
    }

    this.vesselService.deleteVesselData().subscribe({
      next: () => {
        window.alert(this.translate.instant('deleted'));
        this.location.back();
      },
      error: (error: unknown) => {
        console.error('Failed to delete vessel:', error);
        window.alert(this.translate.instant('err'));
      },
    });
  }

  checkDelete(): boolean {
    return window.confirm('Are you sure you want to delete the vessel?');
  }
}
